import React from 'react';
import { Helmet } from 'react-helmet-async';

interface OpenGraphMapMetaProps {
  user: {
    firstName?: string;
    lastName?: string;
    username: string;
    publicUsername?: string;
  } | null;
  mapType: 'memories' | 'states';
  memories: any[];
  visitedStates?: string[];
  mapImageUrl?: string;
}

const OpenGraphMapMeta: React.FC<OpenGraphMapMetaProps> = ({
  user,
  mapType,
  memories,
  visitedStates = [],
  mapImageUrl
}) => {
  if (!user) return null;
  
  const displayName = `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.username;
  const profileUrl = `https://fojourn.site/u/${user.publicUsername || user.username}`;
  const currentUrl = `${profileUrl}?view=${mapType}`;

  // Generate dynamic content based on map type
  const getMetaContent = () => {
    if (mapType === 'memories') {
      return {
        title: `${displayName}'s Travel Memories Map`,
        description: `Explore ${displayName}'s ${memories.length} travel memories and adventures on an interactive map. See where they've been and discover their favorite destinations.`,
        keywords: `travel map, travel memories, ${displayName}, travel journal, travel destinations, ${memories.length} memories`,
        stats: `${memories.length} memories`
      };
    } else {
      const statesList = visitedStates.length > 0 ? visitedStates.join(', ') : 'no states yet';
      return {
        title: `Check out the states I've visited`,
        description: `I've visited ${visitedStates.length} states: ${statesList}`,
        keywords: `states visited, travel map, US states, ${displayName}, travel achievements, ${visitedStates.length} states`,
        stats: `${visitedStates.length} states visited`
      };
    }
  };

  const meta = getMetaContent();

  // Generate image URL based on map type
  const generateImageUrl = () => {
    if (mapImageUrl) {
      return mapImageUrl;
    }
    
    if (mapType === 'states') {
      // Use the composite states map for social sharing - larger size for better quality
      const identifier = user.publicUsername || user.username;
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';
      const baseUrl = API_BASE_URL.replace('/api', '');
      return `${baseUrl}/api/public/composite-states-map/${identifier}?width=1200&height=900&expanded=true`;
    } else {
      // Fallback for memories map
      return `${profileUrl}/map-image?type=${mapType}&width=1200&height=630`;
    }
  };

  const imageUrl = generateImageUrl();

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="keywords" content={meta.keywords} />

      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:image" content={imageUrl} />
      {mapType === 'states' ? (
        <>
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="900" />
        </>
      ) : (
        <>
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
        </>
      )}
      <meta property="og:image:alt" content={`${meta.title} - ${meta.stats}`} />
      <meta property="og:site_name" content="Fojourn" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={`${meta.title} - ${meta.stats}`} />

      {/* Additional Meta Tags */}
      <meta name="author" content={displayName} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={currentUrl} />

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "TravelAction",
          "agent": {
            "@type": "Person",
            "name": displayName,
            "url": profileUrl
          },
          "description": meta.description,
          "url": currentUrl,
          "image": imageUrl,
          ...(mapType === 'memories' && {
            "subEvent": memories.slice(0, 5).map(memory => ({
              "@type": "Event",
              "name": memory.title || memory.description,
              "location": {
                "@type": "Place",
                "geo": {
                  "@type": "GeoCoordinates",
                  "latitude": memory.latitude,
                  "longitude": memory.longitude
                }
              }
            }))
          }),
          ...(mapType === 'states' && {
            "location": visitedStates.slice(0, 10).map(state => ({
              "@type": "Place",
              "name": state,
              "addressRegion": state,
              "addressCountry": "US"
            }))
          })
        })}
      </script>
    </Helmet>
  );
};

export default OpenGraphMapMeta;